import { Checkbox, Table, Tbody, Td, Th, Thead, Tr, type TableCellProps } from '@chakra-ui/react'
import {
  Ellipsis,
  EmptyTablePlaceholder,
  TableSkeleton,
} from 'brokertslib/src/components'
import { userListContext } from 'BrokerTsLib/src/context'
import type { UserGridItem } from 'BrokerTsLib/src/dtos'
import { useContext, useEffect, useState } from 'react'

import {
  EmailAddress,
  TableWrapper,
  UserLink,
  ValidityBadge,
} from '@/components'
import { OrganizationLink } from '@/components/OrganizationLink'

import { TanstackPagination } from '@/components/Grid/TanstackPagination'
import { flexRender, getCoreRowModel, getPaginationRowModel, useReactTable, type ColumnDef, type RowData } from '@tanstack/react-table'
import { UserRolesBadge } from '../../components/User/UserRoleBadge'
import { UserTypeBadge } from '../../components/User/UserTypeBadge'
import { UserListFilter } from './UserListFilter'

export type CustomColumnMeta = {
  cellProps?: TableCellProps
  headerCellProps?: TableCellProps
}

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue = unknown> extends CustomColumnMeta { }
}

export const SelectColumn: ColumnDef<UserGridItem> = {
  id: 'select',
  header: ({ table }) => (
    <Checkbox
      isChecked={table.getIsAllPageRowsSelected()}
      isIndeterminate={table.getIsSomePageRowsSelected()}
      onChange={table.getToggleAllPageRowsSelectedHandler()}
    />
  ),
  cell: ({ row }) => (
    <Checkbox
      isChecked={row.getIsSelected()}
      isIndeterminate={row.getIsSomeSelected()}
      onChange={row.getToggleSelectedHandler()}
    />
  ),
  enableSorting: false,
  enableColumnFilter: false,
  size: 1,
}

export const UserListNameColumn: ColumnDef<UserGridItem> = {
  accessorKey: 'name',
  header: 'Jméno',
  cell: info => {
    const item = info.row.original
    return (
      <UserLink
        firstName={item.firstName}
        lastName={item.lastName}
        name={item.userName}
        userId={item.userId}
        sx={{ color: 'primary.500', fontWeight: 'medium' }}
      />
    )
  },
}

export const UserListOrganizationColumn: ColumnDef<UserGridItem> = {
  accessorKey: 'organization',
  header: 'Organizace',
  cell: info => {
    const item = info.row.original
    return item.organizationUnitId ? (
      <OrganizationLink
        name={item.organizationUnitName}
        ouId={item.organizationUnitId}
      />
    ) : null
  },
}

export const UserListUserNameColumn: ColumnDef<UserGridItem> = {
  accessorKey: 'userName',
  header: 'Uživatelské jméno',
  cell: info => <Ellipsis>{info.row.original.userName}</Ellipsis>,
  meta: { cellProps: { maxW: '150px' } },
}

export const UserListEmailColumn: ColumnDef<UserGridItem> = {
  accessorKey: 'email',
  header: 'E-mail',
  cell: info => <EmailAddress email={info.row.original.email} />,
  meta: { cellProps: { maxW: '150px' } },
}

export const UserListValidityColumn: ColumnDef<UserGridItem> = {
  accessorKey: 'valid',
  header: '',
  cell: info => <ValidityBadge valid={info.row.original.valid} />,
  meta: { cellProps: { p: 1 } },
}

export const UserListTypeColumn: ColumnDef<UserGridItem> = {
  accessorKey: 'type',
  header: '',
  cell: info =>
    info.row.original.userTypeId ? (
      <UserTypeBadge type={info.row.original.userTypeId} />
    ) : (
      ''
    ),
  meta: { cellProps: { pr: 0, py: 0 } },
}

export const UserListRolesColumn: ColumnDef<UserGridItem> = {
  accessorKey: 'roles',
  header: '',
  cell: info =>
    info.row.original.roles ? (
      <UserRolesBadge roles={info.row.original.roles} />
    ) : null,
  meta: { cellProps: { p: 2 } },
}

export const UserListDefaultColumns: ColumnDef<UserGridItem>[] = [
  SelectColumn,
  UserListTypeColumn,
  UserListNameColumn,
  UserListOrganizationColumn,
  UserListUserNameColumn,
  UserListEmailColumn,
  UserListValidityColumn,
  UserListRolesColumn,
]

export function UserList<TGridItem extends UserGridItem>({
  users,
  columnsDefinition = UserListDefaultColumns as ColumnDef<TGridItem>[],
  showFilter = true,
  showPagination = true
}: {
  users: TGridItem[]
  columnsDefinition?: ColumnDef<TGridItem>[]
  showFilter?: boolean
  showPagination?: boolean
}) {
  const {
    loading,
    currentPage,
    pageSize,
    totalCount,
    updateFilter,
  } = useContext(userListContext)

  const pageIndex = currentPage - 1
  const [pagination, setPagination] = useState({
    pageIndex: pageIndex,
    pageSize: pageSize,
  })

  const [rowSelection, setRowSelection] = useState({})

  // Reset výběru při změně stránky
  useEffect(() => {
    setRowSelection({})
  }, [pagination.pageIndex])

  useEffect(() => {
    updateFilter(filter => ({
      ...filter,
      pageIndex: pagination.pageIndex + 1,
      pageSize: pagination.pageSize
    }))
  }, [pagination, updateFilter])

  const table = useReactTable({
    data: users,
    columns: columnsDefinition,
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    state: {
      rowSelection: rowSelection,
      pagination
    },
    enableRowSelection: true,
    onPaginationChange: setPagination,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setRowSelection,
    getPaginationRowModel: getPaginationRowModel(),
  })

  return (
    <>
      {showFilter && (
        <UserListFilter p={6} table={table} />
      )}
      
      <TableWrapper>
        <Table variant="simple" cellPadding="0" cellSpacing="0">
          <Thead>
            {table.getHeaderGroups().map(headerGroup => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Th
                    key={header.id}
                    _empty={{ w: 0, p: 0 }}
                    {...(header.column.columnDef.meta?.headerCellProps ?? {})}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            <TableSkeleton
              rowsCount={pageSize}
              colSpan={table.getVisibleLeafColumns().length}
              isLoaded={!loading}
              rowHeight="40px"
            >
              <EmptyTablePlaceholder
                text="Žádní uživatelé k zobrazení"
                colSpan={table.getVisibleLeafColumns().length}
              >
                {table.getRowModel().rows.map(row => (
                  <Tr key={row.id} height="40px">
                    {row.getVisibleCells().map(cell => (
                      <Td
                        key={cell.id}
                        _empty={{ p: 0 }}
                        {...(cell.column.columnDef.meta?.cellProps ?? {})}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </EmptyTablePlaceholder>
            </TableSkeleton>
          </Tbody>
        </Table>
      </TableWrapper>

      {/* Zobrazit počet záznamů */}
      {showPagination && (
        <TanstackPagination
          table={table}
          maxButtonCount={10}
          totalCount={totalCount}
        />
      )}
    </>
  )
}
